import chalk from 'chalk';
import ora from 'ora';
import { writeFileSync, mkdirSync } from 'node:fs';
import { join } from 'node:path';
import {
  extractFrontmatter,
  resolveChannels,
  lintAll,
  getFramework,
  DEFAULT_FRAMEWORK,
  getTemplate,
  renderTemplate,
  type CharterFrontmatter,
  type ToolsFrontmatter,
  type ChannelId,
  type ChannelPolicy,
  type OrgChannelPolicy,
  type DeploymentTarget,
  type LintDiagnostic,
  type ProvisionInput,
  type TemplateContext,
} from '@augmented/core';
import { provision } from '@augmented/core/provisioning/provisioner.js';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, warn, info } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

function printDiagnostics(diagnostics: LintDiagnostic[]): void {
  for (const d of diagnostics) {
    const prefix = d.severity === 'error' ? chalk.red('ERR') : chalk.yellow('WARN');
    const path = d.path ? chalk.dim(` (${d.path})`) : '';
    console.log(`  ${prefix} [${d.code}] ${d.message}${path}`);
  }
}

/**
 * `agt provision <code-name>`
 */
export async function provisionCommand(
  codeName: string,
  options: { target?: string; output?: string; dryRun?: boolean },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const target = (options.target ?? 'local_docker') as DeploymentTarget;
  const outputDir = options.output ?? join(process.cwd(), '.augmented', codeName, 'provision');
  const dryRun = options.dryRun ?? false;

  if (!json) console.log(chalk.bold('\nAugmented — Provision\n'));

  const spinner = ora({ text: 'Fetching agent data…', isSilent: json });
  spinner.start();

  // ── Fetch all provision data in one call ──────────────────────────────

  let provisionData: {
    agent: Record<string, unknown>;
    charter: { raw_content: string; version: string } | null;
    tools: { raw_content: string; version: string } | null;
    channel_configs: Record<string, { config: unknown; status: string }> | null;
    team_channel_policy: {
      team_id: string;
      allowed_channels: string[];
      denied_channels: string[];
      require_elevated_for_pii: boolean;
    } | null;
  };

  try {
    provisionData = await api.get(`/agents/${encodeURIComponent(codeName)}/provision-data`);
  } catch (err) {
    spinner.fail(`Could not fetch provision data for "${codeName}".`);
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
    return;
  }

  const agentData = provisionData.agent;

  if (!provisionData.charter) {
    spinner.fail('No CHARTER.md version found.');
    if (json) jsonOutput({ ok: false, error: 'No CHARTER.md version found' });
    else error('No CHARTER.md version found for this agent');
    process.exitCode = 1;
    return;
  }

  if (!provisionData.tools) {
    spinner.fail('No TOOLS.md version found.');
    if (json) jsonOutput({ ok: false, error: 'No TOOLS.md version found' });
    else error('No TOOLS.md version found for this agent');
    process.exitCode = 1;
    return;
  }

  spinner.text = 'Parsing frontmatter…';

  const charterContent = provisionData.charter.raw_content;
  const toolsContent = provisionData.tools.raw_content;

  const charterParsed = extractFrontmatter(charterContent);
  if (!charterParsed.frontmatter) {
    spinner.fail('Failed to parse CHARTER.md frontmatter.');
    if (json) jsonOutput({ ok: false, error: charterParsed.error ?? 'Unknown parse error' });
    else error(charterParsed.error ?? 'Unknown parse error');
    process.exitCode = 1;
    return;
  }

  const toolsParsed = extractFrontmatter(toolsContent);
  if (!toolsParsed.frontmatter) {
    spinner.fail('Failed to parse TOOLS.md frontmatter.');
    if (json) jsonOutput({ ok: false, error: toolsParsed.error ?? 'Unknown parse error' });
    else error(toolsParsed.error ?? 'Unknown parse error');
    process.exitCode = 1;
    return;
  }

  const charterFrontmatter = charterParsed.frontmatter as unknown as CharterFrontmatter;
  const toolsFrontmatter = toolsParsed.frontmatter as unknown as ToolsFrontmatter;

  // ── Lint ───────────────────────────────────────────────────────────────

  spinner.text = 'Linting…';
  const lintResult = lintAll(charterContent, toolsContent);

  if (!lintResult.ok) {
    spinner.fail('Lint errors — cannot provision.');
    if (json) {
      jsonOutput({
        ok: false,
        error: 'Lint errors — cannot provision',
        lint: {
          errors: lintResult.errors.length,
          warnings: lintResult.warnings.length,
          diagnostics: [...lintResult.errors, ...lintResult.warnings],
        },
      });
    } else {
      printDiagnostics(lintResult.errors);
      if (lintResult.warnings.length > 0) printDiagnostics(lintResult.warnings);
    }
    process.exitCode = 1;
    return;
  }

  if (lintResult.warnings.length > 0 && !json) {
    spinner.stop();
    warn(`Lint: ${lintResult.warnings.length} warning(s)`);
    printDiagnostics(lintResult.warnings);
    spinner.start('Provisioning…');
  }

  // ── Resolve channels ──────────────────────────────────────────────────

  spinner.text = 'Resolving channels…';

  // Derive channels from agent_channel_configs (source of truth)
  const configuredChannels = Object.keys(provisionData.channel_configs ?? {}) as ChannelId[];

  const agentChannelPolicy: ChannelPolicy = {
    policy: 'allowlist',
    allowed: configuredChannels,
    denied: [],
    require_approval_to_change: true,
  };

  let orgChannelPolicy: OrgChannelPolicy | undefined;
  const policyData = provisionData.team_channel_policy;

  if (policyData) {
    orgChannelPolicy = {
      organization_id: policyData.team_id,
      allowed_channels: (policyData.allowed_channels ?? []) as ChannelId[],
      denied_channels: (policyData.denied_channels ?? []) as ChannelId[],
      require_elevated_for_pii: policyData.require_elevated_for_pii ?? false,
    };
  }

  const resolvedChannels = resolveChannels(agentChannelPolicy, orgChannelPolicy);

  // ── Fetch integrations ────────────────────────────────────────────────
  //
  // ENG-5923: the provision input previously omitted integrations entirely,
  // so `buildMcpJson` defaulted `input.integrations ?? []` and the rendered
  // `.mcp.json` lost every Composio + native (granola, postiz, xero, …) MCP
  // server. Re-provisioning an agent wiped its `.mcp.json` down to the
  // `augmented` server + channel-credential entries. Fetch the decrypted,
  // scope-resolved integrations from the same host endpoint manager-worker
  // uses — credentials are decrypted server-side, so hosts never see
  // ciphertext — and pass them through so the renderer has the full picture.

  spinner.text = 'Fetching integrations…';

  const agentId = agentData.agent_id as string;
  let integrations: NonNullable<ProvisionInput['integrations']> = [];
  let integrationsError: string | null = null;
  try {
    const integrationsData = await api.post<{
      integrations: Array<{
        id: string;
        definition_id: string;
        display_name: string;
        scope: string;
        auth_type: string;
        credentials: Record<string, unknown>;
        config: Record<string, unknown>;
        capabilities: Array<{ id: string; name: string; description: string; access: string }>;
      }>;
    }>('/host/agent-integrations', { agent_id: agentId });
    integrations = (integrationsData.integrations ??
      []) as NonNullable<ProvisionInput['integrations']>;
  } catch (err) {
    // Don't silently strip integrations — that's the exact regression this
    // ticket fixes. An agent with zero integrations returns an empty list
    // (not an error), so only a genuine fetch failure lands here. Surface it
    // loudly and continue rather than writing a stripped `.mcp.json`.
    integrationsError = (err as Error).message;
    if (!json) {
      spinner.stop();
      warn(
        `Could not fetch integrations: ${integrationsError}. ` +
          '.mcp.json may be missing integration servers.',
      );
      spinner.start('Provisioning…');
    }
  }

  // ── Provision ─────────────────────────────────────────────────────────

  const frameworkId = (agentData.framework as string) ?? DEFAULT_FRAMEWORK;
  const adapter = getFramework(frameworkId);

  spinner.text = `Building ${adapter.label} config…`;

  const provisionInput: ProvisionInput = {
    agent: agentData as any,
    charterFrontmatter,
    charterContent,
    toolsFrontmatter,
    toolsContent,
    resolvedChannels,
    integrations,
    deploymentTarget: target,
  };

  const provisionOutput = provision(provisionInput, frameworkId);

  const templateId = target === 'local_docker' ? 'shared-gateway-local' : 'shared-gateway-local';
  const tmpl = getTemplate(templateId);
  let deploymentYaml = '';
  if (tmpl) {
    const templateContext: TemplateContext = {
      agents: [{
        agent_id: agentData.agent_id as string,
        code_name: agentData.code_name as string,
        display_name: agentData.display_name as string,
        environment: agentData.environment as string,
        port: 9000,
      }],
      gateway: { port: 9000 },
      variables: {},
    };
    deploymentYaml = renderTemplate(tmpl.template, templateContext);
  }

  // ── Dry run ───────────────────────────────────────────────────────────

  if (dryRun) {
    spinner.stop();
    if (json) {
      jsonOutput({
        ok: true,
        dry_run: true,
        agent: codeName,
        framework: frameworkId,
        target,
        charter_hash: provisionOutput.charterHash,
        tools_hash: provisionOutput.toolsHash,
        channels: resolvedChannels.length,
        integrations: integrations.length,
        integrations_error: integrationsError,
        artifacts: provisionOutput.artifacts.map((a) => ({
          path: a.relativePath,
          size: a.content.length,
        })),
        deployment_yaml: deploymentYaml || null,
      });
      return;
    }

    console.log();
    info('Dry run — no files written.');
    console.log();
    for (const artifact of provisionOutput.artifacts) {
      console.log(chalk.bold(`${artifact.relativePath}:`));
      console.log(artifact.content);
    }
    if (deploymentYaml) {
      console.log(chalk.bold('Deployment Template:'));
      console.log(deploymentYaml);
    }
    console.log();
    info(`CHARTER hash:  ${provisionOutput.charterHash}`);
    info(`TOOLS hash: ${provisionOutput.toolsHash}`);
    return;
  }

  // ── Write files ───────────────────────────────────────────────────────

  spinner.text = 'Writing files…';

  mkdirSync(outputDir, { recursive: true });
  for (const artifact of provisionOutput.artifacts) {
    writeFileSync(join(outputDir, artifact.relativePath), artifact.content);
  }
  if (deploymentYaml) {
    writeFileSync(join(outputDir, 'docker-compose.yaml'), deploymentYaml);
  }

  // ── Store provision snapshot via API ───────────────────────────────────

  spinner.text = 'Storing provision snapshot…';

  try {
    await api.post(`/agents/${encodeURIComponent(codeName)}/provision-snapshots`, {
      charter_version: provisionData.charter.version,
      tools_version: provisionData.tools.version,
      charter_content: charterContent,
      tools_content: toolsContent,
      channels: resolvedChannels,
    });
  } catch (err) {
    spinner.stop();
    if (!json) warn(`Provision completed but could not store snapshot: ${(err as Error).message}`);
  }

  // ── Summary ───────────────────────────────────────────────────────────

  spinner.succeed(`Agent "${chalk.bold(agentData.display_name as string)}" provisioned.`);

  if (json) {
    jsonOutput({
      ok: true,
      agent: codeName,
      framework: frameworkId,
      target,
      output_dir: outputDir,
      charter_hash: provisionOutput.charterHash,
      tools_hash: provisionOutput.toolsHash,
      channels: resolvedChannels.length,
      integrations: integrations.length,
      integrations_error: integrationsError,
      artifacts: provisionOutput.artifacts.map((a) => a.relativePath),
    });
    return;
  }

  console.log();
  info(`Agent:       ${agentData.code_name}`);
  info(`Framework:   ${adapter.label}`);
  info(`Target:      ${target}`);
  info(`Output:      ${outputDir}`);
  info(`CHARTER hash:   ${provisionOutput.charterHash.slice(0, 12)}…`);
  info(`TOOLS hash:  ${provisionOutput.toolsHash.slice(0, 12)}…`);
  info(`Channels:    ${resolvedChannels.length} active`);
  info(`Integrations: ${integrations.length}${integrationsError ? ' (fetch failed)' : ''}`);
  info(`Artifacts:   ${provisionOutput.artifacts.map((a) => a.relativePath).join(', ')}`);
  console.log();
  info('Next steps:');
  info(`  cd ${outputDir} && docker compose up`);
  info(`  agt drift check ${agentData.code_name}`);
}
